// dev_lp — WIX-style hero section with the live calculation counter.
// Content is identical to the original landing page; only the design is elevated.
// CTA buttons are INERT (dead links) for now.

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getCalculationCounter } from "../../../services/public/publicService";
import { useShouldAnimate } from "../useShouldAnimate";
import { DEV_AUTH_ITEMS, deadLinkHandler } from "../menuConfig";

const HeroSection: React.FC = () => {
  const { t } = useTranslation();
  const shouldAnimate = useShouldAnimate();

  const [count, setCount] = useState<number>(6892);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await getCalculationCounter();
        if (alive && r.res === "OK") setCount(r.total_count);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <section className="relative overflow-hidden">
      {/* decorative blur orbs */}
      <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
        <div
          className={`absolute -top-24 ltr:-left-24 rtl:-right-24 w-96 h-96 rounded-full bg-sky-300/40 blur-3xl ${
            shouldAnimate ? "animate-pulse" : ""
          }`}
        />
        <div
          className={`absolute top-32 ltr:right-0 rtl:left-0 w-80 h-80 rounded-full bg-blue-400/30 blur-3xl ${
            shouldAnimate ? "animate-pulse" : ""
          }`}
          style={shouldAnimate ? { animationDelay: "1.2s" } : undefined}
        />
      </div>

      <div
        className="max-w-5xl mx-auto px-4 sm:px-6 pt-16 pb-20 md:pt-24 md:pb-28 text-center"
        style={shouldAnimate ? { animation: "devlp-rise 0.8s ease-out both" } : undefined}
      >
        <p className="inline-block mb-5 px-4 py-1.5 rounded-full bg-white/70 backdrop-blur border border-blue-100 text-blue-700 text-xs sm:text-sm font-semibold tracking-wide shadow-sm">
          {t("hero.welcome")}
        </p>

        <h1
          className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-tight tracking-tight mb-5 bg-gradient-to-l from-blue-800 via-blue-600 to-sky-500 bg-clip-text text-transparent"
        >
          {t("hero.title")}
        </h1>

        <p className="text-lg md:text-2xl text-gray-700 font-medium max-w-3xl mx-auto leading-relaxed mb-8">
          {t("hero.subtitle")}
        </p>

        {/* live counter badge */}
        <div className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-2xl bg-gradient-to-l from-blue-700 to-sky-500 text-white shadow-xl shadow-blue-900/20">
          <svg className="w-5 h-5 flex-shrink-0 opacity-90" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m-6 4h6m-6 4h4M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2z" />
          </svg>
          <span className={`text-sm md:text-base font-medium transition-opacity ${loading ? "opacity-70" : "opacity-100"}`}>
            {t("hero.counter", { count })}
          </span>
        </div>

        <p className="text-sm md:text-base text-gray-600 mt-7 max-w-2xl mx-auto">
          {t("hero.tagline")}
        </p>

        {/* CTA (inert) */}
        <div className="flex flex-col sm:flex-row gap-3.5 justify-center mt-10">
          <a
            href={DEV_AUTH_ITEMS[0].route}
            data-devlp-route={DEV_AUTH_ITEMS[0].route}
            onClick={deadLinkHandler}
            className="px-8 py-4 rounded-full text-white text-base font-semibold shadow-lg shadow-blue-900/25 bg-gradient-to-l from-blue-700 to-sky-500 hover:shadow-xl hover:-translate-y-0.5 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            {t("cta.register")}
          </a>
          <a
            href={DEV_AUTH_ITEMS[1].route}
            data-devlp-route={DEV_AUTH_ITEMS[1].route}
            onClick={deadLinkHandler}
            className="px-8 py-4 rounded-full text-base font-semibold text-blue-700 bg-white/80 backdrop-blur border-2 border-blue-200 hover:border-blue-400 hover:-translate-y-0.5 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            {t("cta.login")}
          </a>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
